/*
 * =========================================================
 * CHEMARRY CAT PLAY v1.0
 * Juguetes, rascadores y accesorios para gatos
 * Compatible con Shopify Dawn 15.4.1
 * =========================================================
 */

(() => {
  'use strict';

  const SELECTORS = {
    root: 'chemarry-cat-play',
    productList: '[data-play-product-list]',
    productItem: '[data-play-product-item]',
    configuredItem: '[data-play-configured="true"]',
    filter: '[data-play-filter]',
    search: '[data-play-search]',
    count: '[data-play-count]',
    empty: '[data-play-empty]',
    previous: '[data-play-previous]',
    next: '[data-play-next]',
    variantSelect: '[data-play-variant-select]',
    addButton: '[data-play-add]',
    productImage: '[data-play-product-image]',
    price: '[data-play-price]',
    comparePrice: '[data-play-compare-price]',
    availability: '[data-play-availability]',
    toast: '[data-play-toast]'
  };

  const CLASSES = {
    active: 'is-active',
    filtered: 'is-filtered',
    loading: 'is-loading',
    soldOut: 'is-sold-out',
    visible: 'is-visible',
    error: 'is-error'
  };

  const CATEGORY_ALIASES = {
    all: ['all', 'todo', 'todos'],
    scratchers: [
      'scratchers',
      'scratcher',
      'rascadores',
      'rascador',
      'torres',
      'torre',
      'sisal'
    ],
    interactive: [
      'interactive',
      'interactivos',
      'interactivo',
      'estimulo',
      'estímulo',
      'mental',
      'pluma',
      'plumas'
    ],
    toys: [
      'toys',
      'toy',
      'juguetes',
      'juguete',
      'raton',
      'ratón',
      'pelota',
      'pelotas'
    ],
    accessories: [
      'accessories',
      'accessory',
      'accesorios',
      'accesorio',
      'repuesto',
      'repuestos'
    ]
  };

  class ChemarryCatPlay extends HTMLElement {
    constructor() {
      super();

      this.activeFilter = 'all';
      this.searchTerm = '';
      this.toastTimer = null;
      this.resizeTimer = null;
      this.isConnected = false;

      this.handleFilterClick = this.handleFilterClick.bind(this);
      this.handleSearch = this.handleSearch.bind(this);
      this.handlePrevious = this.handlePrevious.bind(this);
      this.handleNext = this.handleNext.bind(this);
      this.handleVariantChange = this.handleVariantChange.bind(this);
      this.handleAddToCart = this.handleAddToCart.bind(this);
      this.handleResize = this.handleResize.bind(this);
      this.handleScroll = this.handleScroll.bind(this);
    }

    connectedCallback() {
      if (this.isConnected) return;

      this.isConnected = true;
      this.cacheElements();
      this.bindEvents();
      this.setInitialState();
      this.applyFilters();
      this.updateArrowState();
    }

    disconnectedCallback() {
      this.unbindEvents();
      this.isConnected = false;

      window.clearTimeout(this.toastTimer);
      window.clearTimeout(this.resizeTimer);
    }

    cacheElements() {
      this.productList = this.querySelector(SELECTORS.productList);
      this.productItems = Array.from(
        this.querySelectorAll(SELECTORS.productItem)
      );
      this.filters = Array.from(this.querySelectorAll(SELECTORS.filter));
      this.searchInput = this.querySelector(SELECTORS.search);
      this.countElement = this.querySelector(SELECTORS.count);
      this.emptyElement = this.querySelector(SELECTORS.empty);
      this.previousButton = this.querySelector(SELECTORS.previous);
      this.nextButton = this.querySelector(SELECTORS.next);
      this.toast = this.querySelector(SELECTORS.toast);
    }

    bindEvents() {
      this.filters.forEach((filter) => {
        filter.addEventListener('click', this.handleFilterClick);
      });

      if (this.searchInput) {
        this.searchInput.addEventListener('input', this.handleSearch);
      }

      if (this.previousButton) {
        this.previousButton.addEventListener('click', this.handlePrevious);
      }

      if (this.nextButton) {
        this.nextButton.addEventListener('click', this.handleNext);
      }

      this.querySelectorAll(SELECTORS.variantSelect).forEach((select) => {
        select.addEventListener('change', this.handleVariantChange);
      });

      this.querySelectorAll(SELECTORS.addButton).forEach((button) => {
        button.addEventListener('click', this.handleAddToCart);
      });

      if (this.productList) {
        this.productList.addEventListener('scroll', this.handleScroll, {
          passive: true
        });
      }

      window.addEventListener('resize', this.handleResize, {
        passive: true
      });
    }

    unbindEvents() {
      this.filters.forEach((filter) => {
        filter.removeEventListener('click', this.handleFilterClick);
      });

      if (this.searchInput) {
        this.searchInput.removeEventListener('input', this.handleSearch);
      }

      if (this.previousButton) {
        this.previousButton.removeEventListener(
          'click',
          this.handlePrevious
        );
      }

      if (this.nextButton) {
        this.nextButton.removeEventListener('click', this.handleNext);
      }

      this.querySelectorAll(SELECTORS.variantSelect).forEach((select) => {
        select.removeEventListener('change', this.handleVariantChange);
      });

      this.querySelectorAll(SELECTORS.addButton).forEach((button) => {
        button.removeEventListener('click', this.handleAddToCart);
      });

      if (this.productList) {
        this.productList.removeEventListener(
          'scroll',
          this.handleScroll
        );
      }

      window.removeEventListener('resize', this.handleResize);
    }

    setInitialState() {
      const activeFilter =
        this.filters.find(
          (filter) =>
            filter.classList.contains(CLASSES.active) ||
            filter.getAttribute('aria-pressed') === 'true'
        ) || this.filters[0];

      if (activeFilter) {
        this.activeFilter = this.normalizeCategory(
          activeFilter.dataset.playFilter || 'all'
        );

        this.setActiveFilter(activeFilter);
      }

      if (this.searchInput) {
        this.searchTerm = this.normalizeText(this.searchInput.value);
      }

      this.querySelectorAll(SELECTORS.variantSelect).forEach((select) => {
        this.updateVariant(select);
      });
    }

    handleFilterClick(event) {
      const selectedFilter = event.currentTarget;

      this.activeFilter = this.normalizeCategory(
        selectedFilter.dataset.playFilter || 'all'
      );

      this.setActiveFilter(selectedFilter);
      this.applyFilters();
      this.resetProductScroll();
    }

    setActiveFilter(selectedFilter) {
      this.filters.forEach((filter) => {
        const isActive = filter === selectedFilter;

        filter.classList.toggle(CLASSES.active, isActive);
        filter.setAttribute('aria-pressed', String(isActive));
      });
    }

    handleSearch(event) {
      this.searchTerm = this.normalizeText(event.target.value);

      this.applyFilters();
      this.resetProductScroll();
    }

    applyFilters() {
      let visibleCount = 0;
      let configuredCount = 0;

      this.productItems.forEach((item) => {
        const isConfigured = item.dataset.playConfigured === 'true';

        if (!isConfigured) {
          const showPlaceholder = this.isThemeEditor();

          item.classList.toggle(CLASSES.filtered, !showPlaceholder);
          item.hidden = !showPlaceholder;
          return;
        }

        configuredCount += 1;

        const categories = this.getItemCategories(item);
        const searchableText = this.normalizeText(
          item.dataset.playSearch || item.textContent || ''
        );

        const matchesCategory =
          this.activeFilter === 'all' ||
          categories.includes(this.activeFilter);

        const matchesSearch =
          this.searchTerm === '' ||
          searchableText.includes(this.searchTerm);

        const shouldShow = matchesCategory && matchesSearch;

        item.classList.toggle(CLASSES.filtered, !shouldShow);
        item.hidden = !shouldShow;

        if (shouldShow) {
          visibleCount += 1;
        }
      });

      this.updateCounter(visibleCount, configuredCount);
      this.updateEmptyState(visibleCount, configuredCount);

      window.requestAnimationFrame(() => {
        this.updateArrowState();
      });
    }

    getItemCategories(item) {
      const rawCategories = item.dataset.playCategories || '';
      const separatedCategories = rawCategories.split(/[|,]/);

      return separatedCategories
        .map((category) => this.normalizeCategory(category))
        .filter(Boolean);
    }

    normalizeCategory(value) {
      const normalizedValue = this.normalizeText(value)
        .replace(/\s+/g, '-')
        .trim();

      const aliasEntry = Object.entries(CATEGORY_ALIASES).find(
        ([, aliases]) =>
          aliases.some(
            (alias) =>
              this.normalizeText(alias).replace(/\s+/g, '-') ===
              normalizedValue
          )
      );

      return aliasEntry ? aliasEntry[0] : normalizedValue;
    }

    normalizeText(value) {
      return String(value || '')
        .toLocaleLowerCase('es')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/\s+/g, ' ')
        .trim();
    }

    updateCounter(visibleCount, configuredCount) {
      if (!this.countElement) return;

      const count = configuredCount === 0 ? 0 : visibleCount;
      const singularText =
        this.countElement.dataset.singular || 'producto';
      const pluralText =
        this.countElement.dataset.plural || 'productos';

      this.countElement.textContent =
        `${count} ${count === 1 ? singularText : pluralText}`;
    }

    updateEmptyState(visibleCount, configuredCount) {
      if (!this.emptyElement) return;

      const showEmpty =
        configuredCount === 0 ||
        (configuredCount > 0 && visibleCount === 0);

      this.emptyElement.hidden = !showEmpty;
    }

    resetProductScroll() {
      if (!this.productList) return;

      this.productList.scrollTo({
        left: 0,
        behavior: this.prefersReducedMotion() ? 'auto' : 'smooth'
      });

      window.requestAnimationFrame(() => {
        this.updateArrowState();
      });
    }

    handlePrevious() {
      this.scrollProducts(-1);
    }

    handleNext() {
      this.scrollProducts(1);
    }

    scrollProducts(direction) {
      if (!this.productList) return;

      const visibleItems = this.getVisibleItems();
      const firstVisibleItem = visibleItems[0];

      if (!firstVisibleItem) return;

      const listStyles = window.getComputedStyle(this.productList);
      const gap =
        parseFloat(listStyles.columnGap || listStyles.gap || 0) || 0;

      const distance = firstVisibleItem.getBoundingClientRect().width + gap;

      this.productList.scrollBy({
        left: direction * distance,
        behavior: this.prefersReducedMotion() ? 'auto' : 'smooth'
      });
    }

    getVisibleItems() {
      return this.productItems.filter(
        (item) =>
          !item.hidden &&
          !item.classList.contains(CLASSES.filtered) &&
          item.dataset.playConfigured === 'true'
      );
    }

    handleScroll() {
      window.requestAnimationFrame(() => {
        this.updateArrowState();
      });
    }

    handleResize() {
      window.clearTimeout(this.resizeTimer);

      this.resizeTimer = window.setTimeout(() => {
        this.updateArrowState();
      }, 100);
    }

    updateArrowState() {
      if (
        !this.productList ||
        !this.previousButton ||
        !this.nextButton
      ) {
        return;
      }

      const isDesktop = window.matchMedia('(min-width: 990px)').matches;

      if (isDesktop) {
        this.previousButton.disabled = true;
        this.nextButton.disabled = true;
        return;
      }

      const maxScroll =
        this.productList.scrollWidth - this.productList.clientWidth;
      const currentScroll = Math.max(0, this.productList.scrollLeft);
      const tolerance = 3;

      this.previousButton.disabled = currentScroll <= tolerance;
      this.nextButton.disabled =
        maxScroll <= tolerance ||
        currentScroll >= maxScroll - tolerance;
    }

    handleVariantChange(event) {
      this.updateVariant(event.currentTarget);
    }

    updateVariant(select) {
      const selectedOption = select.options[select.selectedIndex];

      if (!selectedOption) return;

      const card = select.closest('[data-play-card]');
      if (!card) return;

      const addButton = card.querySelector(SELECTORS.addButton);
      const priceElement = card.querySelector(SELECTORS.price);
      const comparePriceElement = card.querySelector(
        SELECTORS.comparePrice
      );
      const availabilityElement = card.querySelector(
        SELECTORS.availability
      );
      const imageElement = card.querySelector(SELECTORS.productImage);

      const variantId = selectedOption.value;
      const price = selectedOption.dataset.price || '';
      const comparePrice =
        selectedOption.dataset.comparePrice || '';
      const available =
        selectedOption.dataset.available === 'true';
      const imageUrl = selectedOption.dataset.image || '';
      const imageAlt = selectedOption.dataset.imageAlt || '';

      if (addButton) {
        addButton.dataset.variantId = variantId;
        addButton.disabled = !available;
        addButton.classList.toggle(CLASSES.soldOut, !available);

        const availableText =
          addButton.dataset.availableText || 'Agregar al carrito';
        const soldOutText =
          addButton.dataset.soldOutText || 'Agotado';

        addButton.textContent = available
          ? availableText
          : soldOutText;
      }

      if (priceElement && price) {
        priceElement.textContent = price;
      }

      if (comparePriceElement) {
        if (comparePrice) {
          comparePriceElement.textContent = comparePrice;
          comparePriceElement.hidden = false;
        } else {
          comparePriceElement.textContent = '';
          comparePriceElement.hidden = true;
        }
      }

      if (availabilityElement) {
        const availableText =
          availabilityElement.dataset.availableText || 'Disponible';
        const soldOutText =
          availabilityElement.dataset.soldOutText || 'Agotado';

        availabilityElement.textContent = available
          ? availableText
          : soldOutText;

        availabilityElement.classList.toggle(
          CLASSES.soldOut,
          !available
        );
      }

      if (imageElement && imageUrl) {
        imageElement.src = imageUrl;
        imageElement.removeAttribute('srcset');

        if (imageAlt) {
          imageElement.alt = imageAlt;
        }
      }
    }

    async handleAddToCart(event) {
      event.preventDefault();

      const button = event.currentTarget;
      const variantId = Number(button.dataset.variantId);

      if (!variantId || button.disabled) return;

      this.setButtonLoading(button, true);

      try {
        const sectionIds = this.getCartSectionIds();

        const requestBody = {
          items: [
            {
              id: variantId,
              quantity: 1
            }
          ]
        };

        if (sectionIds.length > 0) {
          requestBody.sections = sectionIds.join(',');
          requestBody.sections_url =
            window.location.pathname + window.location.search;
        }

        const response = await fetch(
          `${window.Shopify?.routes?.root || '/'}cart/add.js`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json'
            },
            body: JSON.stringify(requestBody)
          }
        );

        const result = await response.json();

        if (!response.ok) {
          throw new Error(
            result.description ||
              result.message ||
              'No pudimos agregar el producto.'
          );
        }

        await this.refreshCart(result);

        const successMessage =
          button.dataset.successMessage ||
          'Producto agregado al carrito.';

        this.showToast(successMessage, false);
      } catch (error) {
        this.showToast(
          error.message ||
            'No pudimos agregar el producto. Intenta nuevamente.',
          true
        );
      } finally {
        this.setButtonLoading(button, false);
      }
    }

    setButtonLoading(button, isLoading) {
      button.classList.toggle(CLASSES.loading, isLoading);
      button.setAttribute('aria-busy', String(isLoading));

      if (isLoading) {
        button.disabled = true;
        return;
      }

      const card = button.closest('[data-play-card]');
      const select = card?.querySelector(SELECTORS.variantSelect);

      if (select) {
        const selectedOption = select.options[select.selectedIndex];
        button.disabled =
          selectedOption?.dataset.available !== 'true';
      } else {
        button.disabled = button.dataset.available !== 'true';
      }
    }

    getCartSectionIds() {
      const sectionIds = new Set();

      const cartDrawer = document.querySelector('cart-drawer');
      const cartNotification =
        document.querySelector('cart-notification');

      [cartDrawer, cartNotification].forEach((component) => {
        if (
          component &&
          typeof component.getSectionsToRender === 'function'
        ) {
          component.getSectionsToRender().forEach((section) => {
            if (section?.id) {
              sectionIds.add(section.id);
            }
          });
        }
      });

      sectionIds.add('cart-icon-bubble');

      return Array.from(sectionIds);
    }

    async refreshCart(addResult) {
      const cartDrawer = document.querySelector('cart-drawer');
      const cartNotification =
        document.querySelector('cart-notification');

      if (
        addResult.sections &&
        cartDrawer &&
        typeof cartDrawer.renderContents === 'function'
      ) {
        cartDrawer.renderContents(addResult);

        window.setTimeout(() => {
          this.openCartComponent(cartDrawer);
        }, 80);

        return;
      }

      if (
        addResult.sections &&
        cartNotification &&
        typeof cartNotification.renderContents === 'function'
      ) {
        cartNotification.renderContents(addResult);

        if (typeof cartNotification.open === 'function') {
          cartNotification.open();
        }

        return;
      }

      await this.refreshCartSectionsManually();
    }

    async refreshCartSectionsManually() {
      const root = window.Shopify?.routes?.root || '/';
      const sectionIds = this.getCartSectionIds();

      try {
        const response = await fetch(
          `${root}?sections=${encodeURIComponent(
            sectionIds.join(',')
          )}`
        );

        if (!response.ok) throw new Error();

        const sections = await response.json();

        this.replaceCartSection(
          sections,
          'cart-icon-bubble',
          '#shopify-section-cart-icon-bubble'
        );

        this.replaceCartSection(
          sections,
          'cart-drawer',
          '#shopify-section-cart-drawer'
        );

        const cartDrawer = document.querySelector('cart-drawer');

        if (cartDrawer) {
          window.setTimeout(() => {
            this.openCartComponent(cartDrawer);
          }, 80);
        }
      } catch (error) {
        window.location.href = `${root}cart`;
      }
    }

    replaceCartSection(sections, sectionId, selector) {
      const html = sections?.[sectionId];
      if (!html) return;

      const parser = new DOMParser();
      const documentFragment = parser.parseFromString(
        html,
        'text/html'
      );

      const newSection =
        documentFragment.querySelector(selector) ||
        documentFragment.body.firstElementChild;

      const currentSection = document.querySelector(selector);

      if (newSection && currentSection) {
        currentSection.replaceWith(newSection);
      }
    }

    openCartComponent(cartDrawer) {
      if (!cartDrawer) return;

      if (typeof cartDrawer.open === 'function') {
        cartDrawer.open();
        return;
      }

      cartDrawer.classList.add('active');
      document.body.classList.add('overflow-hidden');

      const innerDrawer = cartDrawer.querySelector('.drawer__inner');

      if (innerDrawer) {
        innerDrawer.setAttribute('tabindex', '-1');
        innerDrawer.focus({ preventScroll: true });
      }
    }

    showToast(message, isError = false) {
      if (!this.toast || !message) return;

      window.clearTimeout(this.toastTimer);

      this.toast.textContent = message;
      this.toast.classList.toggle(CLASSES.error, isError);
      this.toast.classList.add(CLASSES.visible);
      this.toast.setAttribute('role', isError ? 'alert' : 'status');

      this.toastTimer = window.setTimeout(() => {
        this.toast.classList.remove(CLASSES.visible);
      }, 3200);
    }

    isThemeEditor() {
      return Boolean(window.Shopify && window.Shopify.designMode);
    }

    prefersReducedMotion() {
      return window.matchMedia(
        '(prefers-reduced-motion: reduce)'
      ).matches;
    }
  }

  if (!customElements.get('chemarry-cat-play')) {
    customElements.define('chemarry-cat-play', ChemarryCatPlay);
  }

  document.addEventListener('shopify:section:load', (event) => {
    const section = event.target;
    const components = section.querySelectorAll(SELECTORS.root);

    components.forEach((component) => {
      if (
        component instanceof ChemarryCatPlay &&
        !component.isConnected
      ) {
        component.connectedCallback();
      }
    });
  });

  document.addEventListener('shopify:block:select', (event) => {
    const item = event.target.closest(SELECTORS.productItem);
    const component = event.target.closest(SELECTORS.root);

    if (!item || !component || !component.productList) return;

    item.scrollIntoView({
      behavior: component.prefersReducedMotion() ? 'auto' : 'smooth',
      block: 'nearest',
      inline: 'center'
    });
  });
})();